/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { useI18n } from 'vue-i18n'

/**
 * Composable for handling photo weight estimation errors consistently
 * across the application (SessionGoatSelectView and WeightList)
 */
export function usePhotoWeightError() {
  const { t } = useI18n()

  /**
   * Get error message and toast type for a photo weight error
   * @param {Object} error - Error object from PhotoWeightView
   * @param {string} error.type - Error type (e.g., 'no-goat', 'invalid-weight')
   * @param {string} error.message - Optional error message
   * @returns {Object} Object with message and type for Toast component
   */
  function getErrorNotification({ type, message }) {
    // Handle all error types from PhotoWeightView with appropriate toasts
    if (type === 'missing-focal-length') {
      return {
        message: t('ml.error.missingFocalLength'),
        subtitle: t('ml.error.missingFocalLengthHint'),
        type: 'warning',
      }
    } else if (type === 'no-goat') {
      return {
        message: t('ml.error.noGoatDetected'),
        subtitle: t('ml.error.noGoatDetectedHint'),
        type: 'error',
      }
    } else if (type === 'model-load-failed') {
      return {
        message: t('ml.error.modelLoadFailed'),
        subtitle: t('ml.error.modelLoadFailedHint'),
        type: 'error',
      }
    } else if (type === 'measurement-failed') {
      return {
        message: t('ml.error.measurementFailed'),
        subtitle: t('ml.error.measurementFailedHint'),
        type: 'error',
      }
    } else if (type === 'invalid-weight') {
      return {
        message: t('ml.error.invalidWeight'),
        subtitle: t('ml.error.invalidWeightHint'),
        type: 'error',
      }
    } else if (type === 'distance-too-far') {
      return {
        message: t('ml.error.distanceTooFar'),
        subtitle: t('ml.error.distanceTooFarHint'),
        type: 'error',
      }
    } else {
      // Generic fallback for any other error type
      const errorMsg = message || t('common.toasts.error')
      console.error('Weight estimation error:', type, message)
      return {
        message: errorMsg,
        subtitle: '',
        type: 'error',
      }
    }
  }

  return {
    getErrorNotification,
  }
}
